/**
 * ░░░░░▄▀▀▀▄░░░░░░░░░░░░░░░░░
 * ▄███▀░◐░░░▌░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▐░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐▄▄░░░░░░░░░░░░░░
 * ░░░░▌░░░░▄▀▒▒▀▀▀▀▄░░░░░░░░░
 * ░░░▐░░░░▐▒▒▒▒▒▒▒▒▀▀▄░░░░░░░
 * ░░░▐░░░░▐▄▒▒▒▒▒▒▒▒▒▒▀▄░░░░░
 * ░░░░▀▄░░░░▀▄▒▒▒▒▒▒▒▒▒▒▀▄░░░
 * ░░░░░░▀▄▄▄▄▄█▄▄▄▄▄▄▄▄▄▄▄▀▄░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░▄▄▌▌▄▌▌░░░░░░░░░░░
 *
 * MIGRATION NOTICE
 *
 * We are in the process of re-writing our components using function components
 * and React Hooks, rather than extending Component.
 *
 * We have found that hooks make our components better, and - once you get the
 * hang of them - produce fewer bugs and edge cases than conventional lifecycle
 * methods (like componentDidMount and getDerivedStateFromProps).
 *
 * However, hooks can’t be used from class components. Therefore, we are trying
 * to eliminate class components wherever we can.
 *
 * If you’re changing this file, please consider contributing to this effort by
 * changing this component to be a function that uses hooks, and then removing
 * this notice.
 */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { css } from 'emotion';

import VerticalSpacing from 'site-react/core/VerticalSpacing';
import theme from 'site-react/theme';

import Context from './context';
import ErrorToast from './ErrorToast';

const wrapperStyle = css`
  left: 0;
  max-height: 100vh;
  overflow-y: auto;
  padding: ${theme.spacing.md}px ${theme.spacing.md}px 0;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${theme.zIndex.toast};
`;

class ErrorHandler extends Component {
  constructor(props) {
    super(props);

    this.addError = this.addError.bind(this);

    this.state = {
      errors: [],
      lastKey: 0,
      value: {
        addError: this.addError,
      },
    };
  }

  addError(error) {
    this.setState((state) => ({
      errors: [
        ...state.errors,
        {
          error,
          key: state.lastKey + 1,
        },
      ],
      lastKey: state.lastKey + 1,
    }));
  }

  removeError(key) {
    this.setState((state) => ({
      errors: [
        ...state.errors.filter(
          ({ key: currentErrorKey }) => key !== currentErrorKey,
        ),
      ],
    }));
  }

  render() {
    const { children } = this.props;
    const { errors, value } = this.state;

    return (
      <Context.Provider value={value}>
        {errors.length > 0 ? (
          <div className={wrapperStyle}>
            {errors.map(({ error, key }) => (
              <VerticalSpacing key={key} size="md">
                <ErrorToast onClose={() => this.removeError(key)}>
                  {error}
                </ErrorToast>
              </VerticalSpacing>
            ))}
          </div>
        ) : null}
        {children}
      </Context.Provider>
    );
  }
}

ErrorHandler.defaultProps = {
  children: null,
};

ErrorHandler.propTypes = {
  /**
   * All the other content to show. It's likely that the whole page should be
   * inside this component.
   */
  children: PropTypes.node,
};

export default ErrorHandler;
